// One square of the ground: a tile with the health it has left, or nothing. A tile type is a
// kind and the health it starts with, so more kinds can be added without changing the rule.

export type TileType = {
  readonly kind: string;
  readonly health: number;
};

export const tile = (kind: string, health: number): TileType => ({ kind, health });

export const GRASS: TileType = tile("grass", 1);
export const DIRT: TileType = tile("dirt", 1.5);

export type Tile = {
  readonly kind: string;
  readonly health: number;
  readonly maxHealth: number;
};

export const place = (type: TileType): Tile => ({
  kind: type.kind,
  health: type.health,
  maxHealth: type.health,
});

export type Cell = Tile | { readonly kind: "air" };
export const AIR: Cell = { kind: "air" };

// An ordinary on-beat swing. A perfect music hit is more; a miss is none.
export const DAMAGE = 1;

export type Direction = "down" | "left" | "right";

export const damageFor = (quality: number): number => {
  if (quality >= 1) {
    return 1.5;
  }
  if (quality >= 0.5) {
    return 1;
  }
  if (quality > 0) {
    return 0.5;
  }
  return 0;
};

export const dig = (cell: Cell, damage: number): Cell => {
  if (!("health" in cell) || cell.health <= damage) {
    return AIR;
  }
  return { kind: cell.kind, health: cell.health - damage, maxHealth: cell.maxHealth };
};

// The ground is the same in every column until someone digs it: sky above the surface, grass on
// it, dirt forever below. What has been dug is a list of wounds, the health each square has left;
// a wound with none left is an open square.
export const SURFACE = 2;

export type Wound = {
  readonly column: number;
  readonly row: number;
  readonly health: number;
};

export const cellAt = (ground: ReadonlyArray<Wound>, column: number, row: number): Cell => {
  if (row < SURFACE) {
    return AIR;
  }
  const type = row === SURFACE ? GRASS : DIRT;
  const wound = ground.find((one) => one.column === column && one.row === row);
  if (wound === undefined) {
    return place(type);
  }
  if (wound.health === 0) {
    return AIR;
  }
  return { kind: type.kind, health: wound.health, maxHealth: type.health };
};

// A player's last blow: the square it landed on, which way it went, and when, in server time.
export type Swing = {
  readonly column: number;
  readonly row: number;
  readonly direction: Direction;
  readonly at: number;
};

// Someone on the site: the slot they took as they arrived, the name they readied up under (empty
// until then), where they stand, and their last blow.
export type Player = {
  readonly slot: number;
  readonly name: string;
  readonly ready: boolean;
  readonly column: number;
  readonly row: number;
  readonly swing: Swing;
};

// Everyone on the site, whether the dig has begun, and what has been dug. Players keep their
// order through every rule below.
export type World = {
  readonly started: boolean;
  readonly players: ReadonlyArray<Player>;
  readonly ground: ReadonlyArray<Wound>;
};

export const EMPTY: World = { started: false, players: [], ground: [] };

// A swing takes a quarter second, and a press while one is going does nothing; a step takes none.
export const SWING_MS = 250;

// Each slot starts four columns from the next, so neighbouring shafts begin with a wall between.
const SPACING = 4;

// Where someone at (column, row) comes to rest: on the first square below that still stands.
const landing = (ground: ReadonlyArray<Wound>, column: number, row: number): number => {
  let floor = row;
  while (!("health" in cellAt(ground, column, floor + 1))) {
    floor++;
  }
  return floor;
};

// Everyone whose floor is gone falls; anyone still standing is left as they were.
const settle = (world: World): World => ({
  ...world,
  players: world.players.map((player) => {
    const row = landing(world.ground, player.column, player.row);
    return row === player.row ? player : { ...player, row };
  }),
});

// A newcomer takes the lowest slot nobody holds and that slot's column, on whatever is left of the
// ground there, unnamed and not ready. Their last blow is long over.
export const arrive = (world: World): Player => {
  let slot = 0;
  while (world.players.some((player) => player.slot === slot)) {
    slot++;
  }
  const column = slot * SPACING;
  const row = landing(world.ground, column, SURFACE - 1);
  return {
    slot,
    name: "",
    ready: false,
    column,
    row,
    swing: { column, row: row + 1, direction: "down", at: 0 },
  };
};

// The dig begins once everyone present is ready, and then stays begun while anyone is there.
export const begin = (world: World): World => {
  if (world.started || world.players.length === 0 || world.players.some((one) => !one.ready)) {
    return world;
  }
  return { ...world, started: true };
};

// A second ready (Enter pressed again before the room answered the first) is the same world back:
// a name is given once.
export const ready = (world: World, player: Player, name: string): World => {
  if (player.ready) {
    return world;
  }
  return begin({
    ...world,
    players: world.players.map((one) =>
      one.slot === player.slot ? { ...one, name, ready: true } : one,
    ),
  });
};

const step = (direction: Direction): readonly [number, number] => {
  switch (direction) {
    case "down":
      return [0, 1];
    case "left":
      return [-1, 0];
    case "right":
      return [1, 0];
  }
  return direction satisfies never;
};

// One press by someone in the dig. Into an open square they step, and fall as far as it lets
// them; at a square that stands they swing, and the blow lands at once. A press before the dig,
// from someone not in it, or during a swing changes nothing, and answers with the same world.
export const act = (world: World, player: Player, direction: Direction, now: number): World => {
  if (!world.started || !player.ready || now < player.swing.at + SWING_MS) {
    return world;
  }
  const [dx, dy] = step(direction);
  const column = player.column + dx;
  const row = player.row + dy;
  const cell = cellAt(world.ground, column, row);
  if (!("health" in cell)) {
    return settle({
      ...world,
      players: world.players.map((one) =>
        one.slot === player.slot ? { ...one, column, row } : one,
      ),
    });
  }
  const left = dig(cell, DAMAGE);
  const swing: Swing = { column, row, direction, at: now };
  return settle({
    ...world,
    players: world.players.map((one) => (one.slot === player.slot ? { ...one, swing } : one)),
    ground: [
      ...world.ground.filter((one) => one.column !== column || one.row !== row),
      { column, row, health: "health" in left ? left.health : 0 },
    ],
  });
};
